<script setup lang="ts">
import { watchEffect } from "vue";
import { useAppTheme } from "@/shared/composables/useAppTheme";
import { usePageSearchShortcut } from "@/shared/composables/usePageSearchShortcut";
import AppUpdatePrompt from "@/shared/components/AppUpdatePrompt.vue";

const { themeMode, isDark, section } = useAppTheme();

// ⌘F / Ctrl+F to the page's own search field, wherever one is marked `v-page-search`.
usePageSearchShortcut();

/*
  Habitus reads the theme off the document root: `dark` selects its dark token
  set, and `data-section` selects the product accent (styles/_habitus.scss). Both
  have to sit on the root rather than on a layout, because Habitus derives its
  surfaces and ink from the accent with declarations on `:root`.

  `data-theme-mode` is the CHOSEN mode, which `dark` cannot express: `system` in
  a dark OS and an explicit `dark` produce the same class and are different
  answers to "which one is selected". Nothing reads it today; it is there for an
  element that cannot reach the state any other way, a portalled one.

  `color-scheme` is set here too, so the browser draws its native controls (a number
  field's spinner, a scrollbar, a date picker) in the page's own scheme, portalled
  overlays included.
*/
watchEffect(() => {
	const root = document.documentElement;
	root.classList.toggle("dark", isDark.value);
	root.dataset.section = section.value;
	root.dataset.themeMode = themeMode.value;
	root.style.colorScheme = isDark.value ? "dark" : "light";
});
</script>

<template>
	<router-view />
	<AppUpdatePrompt />
</template>
